import {
  STITCH_TYPE_TARGETS,
  LAYERS_INTRO_STEP,
  AUTO_FILL_BASELINE_STEP,
  CLOSE_STITCH_PANEL_STEP,
  SHOW_ALL_LAYERS_STEP,
  createLessonIntroStep,
  createExportSteps,
} from '../shared/steps';
import { layers } from './fixture';

// The canvas is always on screen, so it can't tell the tutorial whether a click
// would land. These controls exist only while the app is in the mode the step
// is asking for, which makes them a reliable "you can act now" signal.
const RAIL_SELECTION_ACTIVE = '[data-tutorial="satin-rail-selection-done"]';
const GUIDE_DRAWING_ACTIVE = '[data-tutorial="satin-guide-drawing-done"]';
const RAIL_PATHS = '[data-tutorial="satin-selectable-path"]';
const GUIDE_CONTROLS = '[data-tutorial="satin-guide-controls"]';

/**
 * Satin and offsets, taught on the Simple Flower.
 *
 * Transcribed from a recorded session on this exact artwork: the click
 * positions below are percentages of the canvas and the copy reasons about
 * these particular shapes, so none of it transfers to another design. Steps the
 * learner performs themselves advance on their own click rather than a Next
 * button, so the tutorial keeps pace with the real workflow.
 *
 * The shared prelude — welcome, design picker, conversion, then the Stitch
 * Editor — is prepended by the provider, so this list starts once the editor
 * is open.
 */
const FLOWER_STEPS = [
  createLessonIntroStep({
    title: 'Satin and Offsets',
    summary: 'Three flat colours and a bold outline — the ideal shape for the two moves almost every design needs.',
    objectives: [
      'Give the outline a **satin** stitch',
      '**Offset** the colours so no fabric shows through',
      '**Export** a file you can stitch',
    ],
  }),

  LAYERS_INTRO_STEP,
  AUTO_FILL_BASELINE_STEP,

  {
    id: 'emb-select-layer-3',
    title: 'Pick the Layer to Stitch',
    description: `Click **layer ${layers.outline}** — the **black outline**. It's a narrow band, and narrow bands are what satin is for.`,
    target: `button[aria-label="Select layer ${layers.outline}"]`,
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-layer-selected',
    actionRequired: true,
  },
  {
    id: 'emb-open-stitch-tool',
    title: 'Open the Stitch Settings',
    description: 'Click **Stitch** to choose how this layer is sewn.',
    target: '[data-tutorial="stitch-tool-stitch"]',
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-stitch-menu',
    actionRequired: true,
  },
  {
    id: 'emb-choose-satin',
    title: 'Choose Satin',
    description: '• **Fill** — solid stitching for large areas\n• **Satin** — smooth, glossy bands for borders and lettering\n• **Running** — a thin single line for fine detail\n\nThis outline is a narrow band, so click **Satin**.',
    target: '[data-tutorial="stitch-type-satin"]',
    // All three are outlined while they're being described; the highlight
    // narrows to Satin on the page that asks for the click.
    pageTargets: [
      { multipleTargets: STITCH_TYPE_TARGETS },
      { target: '[data-tutorial="stitch-type-satin"]' },
    ],
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-satin-chosen',
    actionRequired: true,
  },
  {
    id: 'emb-replace-layer',
    title: 'Replace or Keep Both?',
    description: 'Click **Replace this layer** — we\'re redoing this shape as satin from scratch, not stitching satin on top of what\'s there.',
    target: '[data-tutorial="stitch-switch-replace-layer"]',
    position: 'top',
    highlight: true,
    advanceOnTargetClick: true,
    // The prompt only appears when there's existing stitching to replace.
    skipIfMissing: 5000,
    waitForAction: 'emb-replace-confirmed',
    actionRequired: true,
  },
  {
    id: 'emb-select-satin-columns',
    title: 'Tell Satin Where to Run',
    description: 'Satin stitches span **between two edges**. Click **Select Satin Columns** to pick them from your design.',
    target: '[data-tutorial="satin-method-select-satin-columns"]',
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-rail-mode',
    actionRequired: true,
  },
  {
    id: 'emb-pick-edges',
    title: 'Click the Two Edges',
    description: 'Click the outline\'s **two edges** — outside, then inside. Satin fills the band between them.',
    target: '[data-tutorial="stitch-canvas"]',
    position: 'center',
    highlight: true,
    noBackdrop: true,
    // The petal band's outer contour then its inner one. Choosing columns is a
    // path pick rather than a free click, so the markers sit on the strokes
    // themselves and only a hit on one of them counts.
    railPaths: [{ index: 0, at: 0.3 }, { index: 1, at: 0.3 }],
    clickTarget: RAIL_PATHS,
    readyWhen: RAIL_SELECTION_ACTIVE,
    advanceOnTargetClick: true,
    requiredClicks: 2,
    waitForAction: 'emb-edges-picked',
    actionRequired: true,
  },
  {
    id: 'emb-pick-edges-2',
    title: 'Now the Centre Ring',
    description: 'The ring around the yellow centre is a separate band. Click its **two edges** the same way.',
    target: '[data-tutorial="stitch-canvas"]',
    position: 'center',
    highlight: true,
    noBackdrop: true,
    // The centre ring's two contours, the design's other satin band.
    railPaths: [{ index: 2, at: 0.3 }, { index: 3, at: 0.3 }],
    clickTarget: RAIL_PATHS,
    readyWhen: RAIL_SELECTION_ACTIVE,
    advanceOnTargetClick: true,
    requiredClicks: 2,
    waitForAction: 'emb-edges-picked-2',
    actionRequired: true,
  },
  {
    id: 'emb-rails-done',
    title: 'Finish the Columns',
    description: 'Click **Done** — both columns are set.',
    target: '[data-tutorial="satin-rail-selection-done"]',
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-rails-done',
    actionRequired: true,
  },
  {
    id: 'emb-clear-guides',
    title: 'Clear the Ring\'s Guides',
    description: 'Guide lines control the **angle the stitches lie at**. The ring\'s automatic ones fan out unevenly, so click **Clear guides** and we\'ll redo them.',
    // Both buttons live inside the ring's marker, which opens on hover; the
    // step reveals it so the learner sees what they're being asked to click.
    revealBy: `${GUIDE_CONTROLS}[data-guide-pair="1"]`,
    target: '[data-tutorial="satin-clear-guides"][data-guide-pair="1"]',
    position: 'top',
    highlight: true,
    advanceOnTargetClick: true,
    // Nothing to clear if the app didn't generate any for this column.
    skipIfMissing: 6000,
    waitForAction: 'emb-guides-cleared',
    actionRequired: true,
  },
  {
    id: 'emb-add-guides',
    title: 'Add Your Own Guides',
    description: 'Click **Add guides** to start drawing them.',
    revealBy: `${GUIDE_CONTROLS}[data-guide-pair="1"]`,
    target: '[data-tutorial="satin-add-guides"][data-guide-pair="1"]',
    position: 'top',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-guide-mode',
    actionRequired: true,
  },
  {
    id: 'emb-draw-guides',
    title: 'Draw Four Guide Lines',
    description: 'Each guide is **two clicks** — one edge, then the other. Draw **four** around the ring so the stitches point at the centre.',
    target: '[data-tutorial="stitch-canvas"]',
    position: 'center',
    highlight: true,
    noBackdrop: true,
    // Read off the column the learner just built rather than from coordinates
    // recorded on one screen: each guide crosses the band from rail to rail,
    // spread along whatever stretch the side panel and dialogue leave clear.
    guideRungs: { pair: 1, count: 4 },
    clickPairs: true,
    readyWhen: GUIDE_DRAWING_ACTIVE,
    advanceOnTargetClick: true,
    requiredClicks: 8,
    waitForAction: 'emb-guides-drawn',
    actionRequired: true,
  },
  {
    id: 'emb-guides-done',
    title: 'Finish the Guides',
    description: 'Click **Done**. Your satin layer is finished.',
    target: '[data-tutorial="satin-guide-drawing-done"]',
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-guides-done',
    actionRequired: true,
  },

  CLOSE_STITCH_PANEL_STEP,
  SHOW_ALL_LAYERS_STEP,

  {
    id: 'emb-offset-intro',
    title: 'Why Layers Need Offsetting',
    description: 'Thread pulls the fabric, so shapes finish **narrower** than they look — leaving bare fabric between colours. Growing the pink and yellow slightly tucks them **under** the outline.',
    target: '[data-tutorial="stitch-tool-offset"]',
    position: 'bottom',
    showNext: true,
    highlight: true,
  },
  {
    id: 'emb-select-layer-1',
    title: 'Start With the Petals',
    description: `Click **layer ${layers.pink}** — the **pink petals**.`,
    target: `button[aria-label="Select layer ${layers.pink}"]`,
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-layer-1',
    actionRequired: true,
  },
  {
    id: 'emb-open-offset',
    title: 'Open Offset',
    description: 'Click **Offset**.',
    target: '[data-tutorial="stitch-tool-offset"]',
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-offset-open',
    actionRequired: true,
  },
  {
    // Setting the amount and applying it is one motion in one open panel, so
    // this can't be two steps: a learner who does the obvious thing would apply
    // before the tutorial asked, get no acknowledgement, then be told to apply
    // again — growing the layer twice. Offsets accumulate, so that quietly
    // damages the design. Advancing on the Apply click accepts either order.
    id: 'emb-offset-amount',
    title: 'Grow It Slightly',
    description: 'Drag the slider to about **1.4 mm** — enough to close the gap, small enough to keep the shape.\n\nThen click **Apply**.',
    target: '[data-tutorial="offset-apply"]',
    // Highlights the slider while the amount is explained, then the button that
    // commits it.
    pageTargets: [
      { target: '[data-tutorial="offset-amount"]' },
      { target: '[data-tutorial="offset-apply"]' },
    ],
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-offset-applied',
    actionRequired: true,
  },
  {
    id: 'emb-select-layer-2',
    title: 'Now the Yellow Centre',
    description: `Click **layer ${layers.yellow}** — the **yellow centre**.`,
    target: `button[aria-label="Select layer ${layers.yellow}"]`,
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-layer-2',
    actionRequired: true,
  },
  {
    id: 'emb-offset-layer-2',
    title: 'Offset It the Same Way',
    description: 'Same three moves: **Offset**, about **1.2 mm**, then **Apply**. A little less than the petals — it\'s a smaller shape.',
    target: '[data-tutorial="offset-apply"]',
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-offset-applied-2',
    actionRequired: true,
  },

  ...createExportSteps({
    learned: [
      '**Satin** for narrow shapes, steered with your own guide lines',
      '**Offsets** so neighbouring colours overlap instead of gapping',
      '**Export** at the right format and size',
    ],
  }),
];

export default FLOWER_STEPS;
